import React, { useState } from 'react';
import { 
    Box, 
    Alert, 
    Chip,
    Typography, 
    List, 
    ListItem, 
    ListItemText, 
    Divider,
    Paper,
    Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    getPaymentLink,
    getPaywayAccessToken,
    isPaid,
    payButtonText,
    payButtonSx,
    paymentMethodText,
} from '../lib/payments';
import { lineTotal, orderTotal, productItems } from '../lib/orders';
import InCallPayDialog from './cart/InCallPayDialog';
import LeaveForMercadoPagoDialog from './payment/LeaveForMercadoPagoDialog';

// Un pedido armado por el asesor, con su botón de pago.
//
// `inCall`: la card está sobre /call. Ahí pagar no puede navegar (desmontaría
// Call.js y cortaría la sesión de OpenTok), así que Payway se paga en un
// diálogo sobre el video y MercadoPago —que no se puede embeber— abre otra
// pestaña, pero avisando antes de que la llamada sigue en ésta. `onPaid` se
// dispara cuando un pago con tarjeta se aprobó en el acto.
const PurchaseOrder = ({ purchaseOrder, inCall = false, storeName, onPaid }) => {
    const [payOpen, setPayOpen] = useState(false);
    const [leaveOpen, setLeaveOpen] = useState(false);
    const renderItems = items => {
        return (
            <List>
                {items.map((item, index) => {
                    const { quantity, unitPrice, productName, productDescription, variantLabel } = item;
                    const subtotal = lineTotal(item);
                    const secondary = [variantLabel, productDescription].filter(Boolean).join(' · ');
                    return (
                        <ListItem key={index} divider>
                            <ListItemText
                                primary={`${quantity} x ${productName} ($${unitPrice} c/u) - $${subtotal}`}
                                secondary={secondary || null}
                            />
                        </ListItem>
                    );
                })}
            </List>
        );
    };

    const paymentLink = getPaymentLink(purchaseOrder);
    const paid = isPaid(purchaseOrder);
    const provider = purchaseOrder.paymentProvider;
    const paywayToken = inCall ? getPaywayAccessToken(purchaseOrder) : null;

    const openPayment = () => {
        if (provider === 'payway' && paywayToken) {
            setPayOpen(true);
        } else if (provider === 'mercadopago') {
            setLeaveOpen(true);
        } else {
            window.open(paymentLink, '_blank', 'noopener,noreferrer');
        }
    };

    const leaveForMercadoPago = () => {
        setLeaveOpen(false);
        window.open(paymentLink, '_blank', 'noopener,noreferrer');
    };

    const renderPayButton = () => {
        if (paid) {
            return (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        icon={<CheckCircleIcon />}
                        label="Pagado"
                        color="success"
                        sx={{ fontWeight: 700 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        La tienda ya tiene tu pago confirmado.
                    </Typography>
                </Box>
            );
        }
        if (!paymentLink) return null;
        if (inCall) {
            return (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={openPayment}
                        sx={payButtonSx(provider)}
                    >
                        {payButtonText(provider)}
                    </Button>
                </Box>
            );
        }
        return (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    href={paymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={payButtonSx(provider)}
                >
                    {payButtonText(provider)}
                </Button>
            </Box>
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ p: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Método de envío: </strong>{purchaseOrder.shippingOption.description}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Costo de envío: </strong>${purchaseOrder.shippingPrice}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Forma de pago: </strong>{paymentMethodText(purchaseOrder)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Provincia: </strong>{purchaseOrder.province}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Ciudad: </strong>{purchaseOrder.city}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Dirección: </strong>{purchaseOrder.address}
                    </Typography>
                </Alert>
                
                <Typography variant="h6" sx={{ mb: 1 }}>
                    <strong>Items: </strong>
                </Typography>
                {renderItems(productItems(purchaseOrder))}
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="h6" sx={{ mb: 2 }}>
                    <strong>* TOTAL: ${orderTotal(purchaseOrder)}</strong>
                </Typography>
                
                {renderPayButton()}
            </Paper>

            {inCall && (
                <>
                    <InCallPayDialog
                        open={payOpen}
                        onClose={() => setPayOpen(false)}
                        purchaseOrderId={purchaseOrder.purchaseOrderId}
                        accessToken={paywayToken}
                        onApproved={() => {
                            if (onPaid) onPaid(purchaseOrder.purchaseOrderId);
                        }}
                    />
                    <LeaveForMercadoPagoDialog
                        open={leaveOpen}
                        storeName={storeName}
                        onClose={() => setLeaveOpen(false)}
                        onConfirm={leaveForMercadoPago}
                    />
                </>
            )}
        </Box>
    );
};

export default PurchaseOrder;
